import { ReactNode, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { card, colors, fontFamily, type } from "../styles";

export interface Column<T> {
  key: string;
  label: string;
  /** Right-aligns and applies tabular figures. */
  numeric?: boolean;
  /** Omit to make the column unsortable (a pure render column). */
  sortValue?: (row: T) => string | number;
  render: (row: T) => ReactNode;
  width?: number | string;
}

type SortDirection = "asc" | "desc";

/**
 * Sortable table with a sticky header. Sorting is client-side over the single
 * fetched page — at a few hundred rows that's instant, and it keeps every
 * sort/filter combination off Firestore's composite-index list.
 */
const DataTable = <T,>({
  rows,
  columns,
  rowKey,
  onRowClick,
  initialSort,
  emptyLabel = "No rows match these filters.",
}: {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  initialSort?: { key: string; direction: SortDirection };
  emptyLabel?: string;
}) => {
  const [sort, setSort] = useState<{ key: string; direction: SortDirection } | undefined>(
    initialSort
  );
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find(item => item.key === sort.key);
    if (!column?.sortValue) return rows;

    const factor = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = column.sortValue!(a);
      const right = column.sortValue!(b);
      if (typeof left === "number" && typeof right === "number") {
        return (left - right) * factor;
      }
      return String(left).localeCompare(String(right)) * factor;
    });
  }, [rows, columns, sort]);

  const toggleSort = (key: string) => {
    setSort(current =>
      current?.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "desc" }
    );
  };

  return (
    <div style={{ ...card, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily }}>
          <thead>
            <tr>
              {columns.map(column => {
                const sortable = Boolean(column.sortValue);
                const active = sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    onClick={sortable ? () => toggleSort(column.key) : undefined}
                    style={{
                      ...type.bodySmallMedium,
                      color: active ? colors.ink : colors.inkFaint,
                      textAlign: column.numeric ? "right" : "left",
                      padding: "10px 16px",
                      background: colors.surfaceMuted,
                      borderBottom: `1px solid ${colors.border}`,
                      cursor: sortable ? "pointer" : "default",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                      width: column.width,
                      userSelect: "none",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 3,
                        flexDirection: column.numeric ? "row-reverse" : "row",
                      }}
                    >
                      {column.label}
                      {active && (
                        <Icon
                          icon={sort!.direction === "asc" ? "ion:chevron-up" : "ion:chevron-down"}
                          height={11}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => {
              const key = rowKey(row);
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onMouseEnter={() => setHoveredRow(key)}
                  onMouseLeave={() => setHoveredRow(undefined)}
                  style={{
                    cursor: onRowClick ? "pointer" : "default",
                    background: hoveredRow === key ? colors.surfaceMuted : colors.surface,
                    transition: "background-color 0.1s",
                  }}
                >
                  {columns.map(column => (
                    <td
                      key={column.key}
                      style={{
                        ...type.bodyRegular,
                        color: colors.ink,
                        padding: "10px 16px",
                        borderBottom: `1px solid ${colors.border}`,
                        textAlign: column.numeric ? "right" : "left",
                        fontVariantNumeric: column.numeric ? "tabular-nums" : undefined,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedRows.length === 0 && (
        <div
          style={{
            ...type.bodyRegular,
            color: colors.inkFaint,
            padding: "32px 16px",
            textAlign: "center",
          }}
        >
          {emptyLabel}
        </div>
      )}
    </div>
  );
};

export default DataTable;
